'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  AlarmClock, CalendarClock, CircleCheck, Hourglass, TimerOff, TriangleAlert,
} from 'lucide-react';
import { avisoError } from '@/lib/avisoCentral';
import { api } from '@/lib/api';
import HeroVista from '@/components/HeroVista';
import StatCard from '@/components/StatCard';
import { labelRol } from '@/lib/roles';
import { labelArea } from '@/lib/usuarios';
import { cn, colorAvatar, iniciales } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

// Reportes → Plazos: semáforo de vencimiento de los contratos del personal
// (GET /reportes/plazos-contratos). El backend, además de listar, genera los
// AVISOS de la campana para el Director de Instrucción con los umbrales
// acordados con el cliente: 1 mes, 15 días y 1 semana (y el ya vencido),
// indicando cuánto tiempo queda y de qué persona es el contrato.

type Semaforo = 'VENCIDO' | 'SEMANA' | 'QUINCE' | 'MES' | 'VIGENTE';

interface PlazoContrato {
  id: string;
  nombre: string;
  apellido: string;
  rol: string;
  area?: string | null;
  numero_documento: string;
  numero_contrato: string;
  fecha_inicio_contrato?: string | null;
  fecha_fin: string;
  tiene_adicion: boolean;
  dias_restantes: number;
  semaforo: Semaforo;
  // Trazabilidad del registro: quién lo creó / editó y cuándo (fecha y hora).
  creado_por_nombre?: string | null;
  fecha_creacion?: string | null;
  modificado_por_nombre?: string | null;
  fecha_modificacion?: string | null;
}

const SEMAFORO: Record<Semaforo, { label: string; clase: string }> = {
  VENCIDO: { label: 'Vencido', clase: 'border-transparent bg-destructive text-white' },
  SEMANA: { label: '≤ 1 semana', clase: 'border-red-200 bg-red-100 text-red-700' },
  QUINCE: { label: '≤ 15 días', clase: 'border-amber-200 bg-amber-100 text-amber-700' },
  MES: { label: '≤ 1 mes', clase: 'border-yellow-200 bg-yellow-100 text-yellow-800' },
  VIGENTE: { label: 'Vigente', clase: 'border-primary/20 bg-primary/10 text-primary' },
};

const fechaCorta = (iso?: string | null) =>
  iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—';

/** Fecha y hora legibles (trazabilidad): dd/mm/aaaa, hh:mm. */
function fechaHora(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('es-CO')}, ${d.toLocaleTimeString('es-CO', {
    hour: '2-digit', minute: '2-digit',
  })}`;
}

function textoDias(d: number): string {
  if (d < 0) return `Venció hace ${-d === 1 ? '1 día' : `${-d} días`}`;
  if (d === 0) return 'Vence HOY';
  return d === 1 ? 'Queda 1 día' : `Quedan ${d} días`;
}

export default function PlazosContratos() {
  const [datos, setDatos] = useState<PlazoContrato[]>([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState<Semaforo | 'TODOS'>('TODOS');

  useEffect(() => {
    (async () => {
      try {
        setDatos(await api<PlazoContrato[]>('/reportes/plazos-contratos'));
      } catch (e) {
        avisoError('No se pudieron cargar los plazos de contratos', e instanceof Error ? e.message : undefined);
      } finally {
        setCargando(false);
      }
    })();
  }, []);

  const conteo = useMemo(() => {
    const c: Record<Semaforo, number> = { VENCIDO: 0, SEMANA: 0, QUINCE: 0, MES: 0, VIGENTE: 0 };
    for (const d of datos) c[d.semaforo] += 1;
    return c;
  }, [datos]);

  const visibles = filtro === 'TODOS' ? datos : datos.filter((d) => d.semaforo === filtro);

  // ALERTA de vencimientos (pedido del cliente): todo lo que no está vigente —
  // ya vencido o por vencer dentro del mes — ordenado por urgencia.
  const porVencer = useMemo(
    () => datos.filter((d) => d.semaforo !== 'VIGENTE').sort((a, b) => a.dias_restantes - b.dias_restantes),
    [datos],
  );

  // Clic en una tarjeta: filtra por ese semáforo (clic de nuevo = quitar filtro).
  const alternar = (s: Semaforo) => setFiltro((f) => (f === s ? 'TODOS' : s));

  return (
    <div className="space-y-6">
      <HeroVista
        icono={CalendarClock}
        titulo="Reporte de contratos"
        descripcion="Personal con datos de contrato: plazos y vencimientos. Además, el Director de Instrucción recibe aviso en la campana con 1 mes, 15 días y 1 semana de anticipación (y al vencerse), indicando de quién es el contrato y cuánto tiempo queda."
      />

      {/* ALERTA de vencimientos: quiénes están vencidos o por vencer. */}
      {!cargando && porVencer.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <TriangleAlert className="mt-0.5 size-5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {porVencer.length === 1
                  ? 'Hay 1 contrato vencido o por vencer'
                  : `Hay ${porVencer.length} contratos vencidos o por vencer`}
              </p>
              <ul className="mt-2 space-y-1">
                {porVencer.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-amber-900">
                    <span className="font-medium">{d.nombre} {d.apellido}</span>
                    <span className="text-amber-700">({labelRol(d.rol)})</span>
                    <span className="text-amber-700">· N.º {d.numero_contrato}</span>
                    <span className="text-amber-700">· termina {fechaCorta(d.fecha_fin)}</span>
                    <Badge variant="outline" className={cn('ml-auto', SEMAFORO[d.semaforo].clase)}>
                      {textoDias(d.dias_restantes)}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        <StatCard label="Vencidos" value={conteo.VENCIDO} icon={TimerOff} tone="red" loading={cargando} onClick={() => alternar('VENCIDO')} />
        <StatCard label="≤ 1 semana" value={conteo.SEMANA} icon={AlarmClock} tone="red" loading={cargando} onClick={() => alternar('SEMANA')} />
        <StatCard label="≤ 15 días" value={conteo.QUINCE} icon={Hourglass} tone="amber" loading={cargando} onClick={() => alternar('QUINCE')} />
        <StatCard label="≤ 1 mes" value={conteo.MES} icon={CalendarClock} tone="amber" loading={cargando} onClick={() => alternar('MES')} />
        <StatCard label="Vigentes" value={conteo.VIGENTE} icon={CircleCheck} tone="primary" loading={cargando} onClick={() => alternar('VIGENTE')} />
      </div>

      {filtro !== 'TODOS' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          Mostrando: <Badge variant="outline" className={SEMAFORO[filtro].clase}>{SEMAFORO[filtro].label}</Badge>
          <Button type="button" variant="ghost" size="sm" onClick={() => setFiltro('TODOS')}>
            Quitar filtro
          </Button>
        </div>
      )}

      <Card className="py-2">
        <CardContent className="px-2">
          {cargando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : visibles.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {datos.length === 0
                ? 'No hay personal activo con contrato registrado.'
                : 'No hay contratos en ese estado.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Persona</TableHead>
                  <TableHead className="hidden md:table-cell">Área</TableHead>
                  <TableHead>Contrato</TableHead>
                  <TableHead className="hidden sm:table-cell">Termina</TableHead>
                  <TableHead>Tiempo restante</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="hidden lg:table-cell">Trazabilidad</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibles.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="size-9 shrink-0">
                          <AvatarFallback className={cn('text-xs font-semibold', colorAvatar(d.id))}>
                            {iniciales(`${d.nombre} ${d.apellido}`)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{d.nombre} {d.apellido}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {labelRol(d.rol)} · CC {d.numero_documento}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden text-sm md:table-cell">{labelArea(d.area)}</TableCell>
                    <TableCell className="text-sm font-medium">N.º {d.numero_contrato}</TableCell>
                    <TableCell className="hidden text-sm sm:table-cell">
                      {fechaCorta(d.fecha_fin)}
                      {d.tiene_adicion && (
                        <span className="block text-xs text-muted-foreground">con adición</span>
                      )}
                    </TableCell>
                    <TableCell className={cn(
                      'text-sm font-medium',
                      d.dias_restantes < 0 ? 'text-destructive'
                        : d.dias_restantes <= 15 ? 'text-amber-600' : '',
                    )}>
                      {textoDias(d.dias_restantes)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={SEMAFORO[d.semaforo].clase}>
                        {SEMAFORO[d.semaforo].label}
                      </Badge>
                    </TableCell>
                    {/* Quién creó el registro y quién lo editó por última vez. */}
                    <TableCell className="hidden text-xs text-muted-foreground lg:table-cell">
                      <span className="block">
                        Creó: <span className="font-medium text-foreground">{d.creado_por_nombre ?? '—'}</span>
                        {' · '}{fechaHora(d.fecha_creacion)}
                      </span>
                      <span className="block">
                        {d.modificado_por_nombre ? (
                          <>
                            Editó: <span className="font-medium text-foreground">{d.modificado_por_nombre}</span>
                            {' · '}{fechaHora(d.fecha_modificacion)}
                          </>
                        ) : (
                          'Sin ediciones'
                        )}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
